import React from 'react';
import {
  Keyboard,
  Mouse,
  Smartphone,
  Gamepad2,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { useLivePreviewStore } from '../../store/livePreviewStore';

export const InputSimulator: React.FC = () => {
  const {
    inputSimulationEnabled,
    touchSimulationEnabled,
    inputState,
    toggleInputSimulation,
    toggleTouchSimulation,
    simulateKeyPress,
  } = useLivePreviewStore();

  // Common game keys for quick simulation
  const commonKeys = [
    { code: 'KeyW', label: 'W', group: 'movement' },
    { code: 'KeyA', label: 'A', group: 'movement' },
    { code: 'KeyS', label: 'S', group: 'movement' },
    { code: 'KeyD', label: 'D', group: 'movement' },
    { code: 'Space', label: 'Space', group: 'action' },
    { code: 'ShiftLeft', label: 'Shift', group: 'action' },
    { code: 'KeyE', label: 'E', group: 'action' },
    { code: 'KeyQ', label: 'Q', group: 'action' },
    { code: 'Escape', label: 'Esc', group: 'system' },
    { code: 'Enter', label: 'Enter', group: 'system' },
  ];

  return (
    <div className="p-3 space-y-4 max-h-80 overflow-y-auto">
      <h4 className="text-sm font-medium text-zinc-400">Input Simulation</h4>

      {/* Toggle buttons */}
      <div className="flex gap-2">
        <button
          onClick={toggleInputSimulation}
          className={cn(
            'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded text-sm',
            inputSimulationEnabled
              ? 'bg-blue-600 text-white'
              : 'bg-zinc-700 hover:bg-zinc-600'
          )}
        >
          <Keyboard size={16} />
          <span>Keyboard/Mouse</span>
        </button>

        <button
          onClick={toggleTouchSimulation}
          className={cn(
            'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded text-sm',
            touchSimulationEnabled
              ? 'bg-blue-600 text-white'
              : 'bg-zinc-700 hover:bg-zinc-600'
          )}
        >
          <Smartphone size={16} />
          <span>Touch</span>
        </button>
      </div>

      {/* Quick keys */}
      {inputSimulationEnabled && (
        <div>
          <div className="text-xs text-zinc-400 mb-2">Quick Keys</div>
          <div className="flex flex-wrap gap-1">
            {commonKeys.map((key) => (
              <button
                key={key.code}
                onClick={() => simulateKeyPress(key.code)}
                className={cn(
                  'px-2 py-1 rounded text-xs font-mono min-w-[32px]',
                  inputState.keys.has(key.code)
                    ? 'bg-blue-600 text-white'
                    : 'bg-zinc-700 hover:bg-zinc-600'
                )}
              >
                {key.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Current input state */}
      <div className="text-xs space-y-1">
        <div className="flex items-center gap-2">
          <Mouse size={12} className="text-zinc-400" />
          <span className="text-zinc-400">Position:</span>
          <span>
            {inputState.mousePosition.x}, {inputState.mousePosition.y}
          </span>
        </div>

        {inputState.keys.size > 0 && (
          <div className="flex items-center gap-2">
            <Keyboard size={12} className="text-zinc-400" />
            <span className="text-zinc-400">Keys:</span>
            <span className="font-mono">
              {Array.from(inputState.keys).join(' + ')}
            </span>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Gamepad2 size={12} className="text-zinc-400" />
          <span className="text-zinc-400">Gamepad:</span>
          <span className={inputState.gamepadConnected ? 'text-green-400' : 'text-zinc-500'}>
            {inputState.gamepadConnected ? 'Connected' : 'Not connected'}
          </span>
        </div>
      </div>
    </div>
  );
};
